import { useState, useEffect, useReducer, useContext } from 'react';
import PropTypes from 'prop-types';
import ShownPopupsContainer from '../../../containers/shown_popups_container';
import ReferralContext from '../../../contexts/referral';
import { useLocation } from 'react-router';

const POPUP_CHECK_STATUS_TIMEOUT_MS = 1000;

const isRouteWhitelistedForPopups = (route) => {
  if (route === '/') return true;
  // Disable on product pages
  // if (route.startsWith('/shopping/')) return true;
  if (route.startsWith('/men/')) return true;
  if (route.startsWith('/women/')) return true;
  if (route.startsWith('/seller/')) return true;
  if (route === '/sellers') return true;
  if (route === '/about-us') return true;
  if (route === '/how-it-works') return true;
  if (route === '/cart') return true;
  if (route === '/favorites') return true;
  if (route === '/nye-2023-2024') return true;
  return false;
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'set-scroll-interval':
      return {
        ...state,
        timePassed: state.timePassed + POPUP_CHECK_STATUS_TIMEOUT_MS,
        maxScrollY: Math.max(state.scrollY, action.bodyScrollTop)
      };
    default:
      throw new Error('Unhandled action type in zoom and pinch reducer ' + action.type);
  }
};

const ContextAwarePopup = (props) => {
  const {
    children,
    backoffPeriodSchedule,
    minimumDelayToShow,
    minimumScrollToShow,
    shownPopupsContainer,
    shouldShowAutomatically,
    showing,
    setShowing,
    tag,
    timeToAppear
  } = props;

  const [state, dispatch] = useReducer(reducer, {
    timePassed: 0,
    maxScrollY: 0
  });
  const { timePassed, maxScrollY } = state;
  const location = useLocation();
  const referralContainer = useContext(ReferralContext);

  const [willShowThisSession, setWillShowThisSession] = useState(() => {
    // Special case: Anyone referred by Ghina Ghandour cannot see popups.
    if (referralContainer.get() === 'ghinaghandourbyg' ||
        referralContainer.get() === 'ghina-ghandour-by-g') return false;
    // If the popup needs no further action, there is no reason to show it.
    if (!shownPopupsContainer.popupNeedsFurtherAction(tag)) return false;
    // Otherwise, we will definitely show the popup if it was never shown before.
    if (!shownPopupsContainer.wasPopupEverShown(tag)) return true;
    // Okay, this is not the first time. We can display it again if the backoff schedule allows it.
    const showCount = shownPopupsContainer.getPopupShowCount(tag);
    if (showCount > backoffPeriodSchedule.length) return false;
    // Okay, our backoff schedule allows for it. Let's see if enough time has passed.
    const lastShownTime = shownPopupsContainer.lastPopupShowTime(tag);
    const nowTime = (new Date()).getTime();
    return nowTime > lastShownTime + backoffPeriodSchedule[showCount - 1];
  });

  // This will keep track of time, and the maximum scroll Y
  useEffect(() => {
    const timer = setInterval(() => {
      dispatch({
        type: 'set-scroll-interval',
        bodyScrollTop: document.body.scrollTop
      });
    }, POPUP_CHECK_STATUS_TIMEOUT_MS);
    return () => clearInterval(timer);
  }, []);

  // This will run on each new tick.
  const pathName = location.pathname;
  useEffect(() => {
    if (shownPopupsContainer.hasUserRequestToShow(tag)) {
      shownPopupsContainer.popUserRequestToShow(tag);
      setShowing(true);
      setWillShowThisSession(false);
      return;
    }
    if (!willShowThisSession) return;
    if (!shouldShowAutomatically) return;
    if (timePassed < minimumDelayToShow) return;
    if (maxScrollY < minimumScrollToShow) return;
    if (!isRouteWhitelistedForPopups(pathName)) return;

    // Okay:
    //  a) A sufficient amount of time has passed.
    //  b) A sufficient amount of scroll interaction has happened.
    //  c) The popup is expected to show up this session.
    // Thus, we can show the popup, and make it never show again this session.
    setShowing(true);
    setWillShowThisSession(false);
  }, [timePassed, maxScrollY, willShowThisSession, minimumDelayToShow, minimumScrollToShow, setShowing, pathName,
      shownPopupsContainer, shouldShowAutomatically, tag]);

  // This will ensure the shown popups container is up to date whenever this popup is shown.
  useEffect(() => {
    if (!showing) return;
    shownPopupsContainer.recordPopupShow(tag, (new Date()).getTime());
  }, [showing, shownPopupsContainer, tag]);

  useEffect(() => {
    const lastDateShown = new Date(shownPopupsContainer.lastPopupShowTime(tag));
    const currentDate = new Date();
    // convert python date to js date
    const date = timeToAppear.split('T');
    const timeDelay = new Date(date[0] + ' ' + date[1].slice(0, -6));
    if (currentDate >= Date.parse(timeDelay) && lastDateShown <= Date.parse(timeDelay)) setShowing(true);
  }, [shownPopupsContainer, timeToAppear, setShowing, tag]);

  return children;
};

if (process.env.NODE_ENV !== 'production') {
  ContextAwarePopup.propTypes = {
    /* Whether or not the popup is currently showing. */
    showing: PropTypes.bool.isRequired,
    /* A function that allows this component to modify whether or not the popup is showing. */
    setShowing: PropTypes.func.isRequired,
    /* A sequence of times, representing the minimum amount of time that must pass before showing
       an already shown popup. Each number must be provided in milliseconds. Successive numbers
       in the list represent the backoff periods corresponding to each show count. For example,
       if the schedule is [1, 2, 5], the backoff period will be 1ms if the popup was shown once
       before, 2ms if it was shown twice before and 5ms if it was shown thrice before. It will
       be infinity afterwards. */
    backoffPeriodSchedule: PropTypes.arrayOf(PropTypes.number.isRequired).isRequired,
    /* The minimum amount of time that must pass within a session before showing a popup we intend
       to show, in milliseconds. */
    minimumDelayToShow: PropTypes.number.isRequired,
    /* The minimum amount a user needs to scroll on the page in order for the popup to trigger.
       Must be given in pixels. */
    minimumScrollToShow: PropTypes.number.isRequired,
    /* The textual tag representing the popup. */
    tag: PropTypes.string.isRequired,
    shownPopupsContainer: PropTypes.instanceOf(ShownPopupsContainer).isRequired
  };
}

export default ContextAwarePopup;
